import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Box, IconButton, styled, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

import { DraggableItem } from '../../LayoutManager';
import { useAppSelector, type RootState } from '../../redux-store';
import {
  blockSelectors,
  sectionSelectors,
  templateSelectors,
  useIsApplying,
  usePreviewTemplateId,
  useSelectWidgetInstanceEntities,
} from '../../slices';
import {
  canAddWidget,
  getWidgetLimit,
  widgetRegistry,
  type WidgetType,
} from '../../widgetManager';

import { getWidgetDisplayName, getWidgetPreview } from './widgetPalettePreview';

const PREFIX = 'TSMED-WidgetPalette';

const classes = {
  itemDisabled: `${PREFIX}-item-disabled`,
  toggleButton: `${PREFIX}-toggleButton`,
  overlay: `${PREFIX}-overlay`,
  overlayDisabled: `${PREFIX}-overlay-disabled`,
  badge: `${PREFIX}-badge`,
};

const Wrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'visible',
})<{ visible: boolean }>(({ theme: { spacing, palette }, visible }) => ({
  display: visible ? 'flex' : 'none',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  position: 'relative',
  paddingBottom: spacing(2),

  [`.${classes.toggleButton}`]: {
    position: 'absolute',
    bottom: 0,
    left: '50%',
    zIndex: 101,
    backgroundColor: palette.mapBlue[300],
    color: palette.primary.contrastText,
    width: '36px',
    height: '36px',
    transition: 'background-color 0.2s',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
    '&:hover': {
      backgroundColor: palette.mapBlue[400],
    },
  },
}));

const Root = styled(Box, {
  shouldForwardProp: prop => prop !== 'isExpanded',
})<{ isExpanded: boolean }>(({ theme: { spacing }, isExpanded }) => ({
  display: 'flex',
  maxWidth: '95%',
  flexGrow: 1,
  flexDirection: 'row',
  justifyContent: 'flex-start',
  gap: spacing(2),
  padding: spacing(2),
  maxHeight: isExpanded ? '300px' : '0px',
  opacity: isExpanded ? 1 : 0,
  pointerEvents: isExpanded ? 'auto' : 'none',
  transition: 'height 0.3s ease-in-out, opacity 0.3s ease-in-out',
  overflowY: 'hidden',
  overflowX: 'auto',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },

  [`& .${classes.itemDisabled}`]: {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },
}));

const WidgetItemWrapper = styled(Box)(({ theme: { spacing, palette } }) => ({
  position: 'relative',
  display: 'inline-flex',

  // Overlay for disabled widgets (limit reached)
  [`& .${classes.overlayDisabled}`]: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    pointerEvents: 'none',
  },

  // Badge showing available count
  [`& .${classes.badge}`]: {
    position: 'absolute',
    top: spacing(0.5),
    right: spacing(0.5),
    backgroundColor: palette.mapBlue[300],
    color: palette.common.white,
    borderRadius: '12px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: 600,
    zIndex: 100,
    pointerEvents: 'none',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
  },
}));

export const WidgetPalette = () => {
  const [isExpanded, setIsExpanded] = useState(true);

  const activeTemplateIsCustom = useAppSelector(
    templateSelectors.selectActiveTemplateIsCustom,
  );
  const previewTemplateId = usePreviewTemplateId();
  const isApplying = useIsApplying();
  const templates = useAppSelector((state: RootState) =>
    templateSelectors.selectAll(state),
  );
  const widgetInstances = useSelectWidgetInstanceEntities();
  const sectionsById = sectionSelectors.useSelectSectionEntities();

  // Determine if current template is custom (preview or active)
  const previewTemplate = templates.find(t => t.id === previewTemplateId);
  const isCustom = previewTemplate?.isCustom ?? activeTemplateIsCustom;

  // Hide widget palette if not custom OR if applying
  const visible = isCustom && !isApplying;

  // Check if new-cases aside is visible (counts as a new-cases widget)
  const hasNewCasesAside = blockSelectors.useSelectNewCasesBlocksVisible();

  const handleToggle = () => {
    setIsExpanded(prev => !prev);
  };

  // Count only ACTIVE/VISIBLE widgets (those actually placed in sections)
  const widgetCounts = useMemo(() => {
    const placedWidgetIds = new Set<string>();
    Object.values(sectionsById).forEach(section => {
      if (section?.blockOrder) {
        section.blockOrder.forEach(widgetId => placedWidgetIds.add(widgetId));
      }
    });

    const counts: Record<
      string,
      { total: number; bySection: Record<string, number> }
    > = {};
    Object.values(widgetInstances).forEach(instance => {
      if (instance && placedWidgetIds.has(instance.id)) {
        // Skip sealed new-cases widgets (they are hidden when aside is on)
        if (instance.sealed && instance.type === 'new-cases') {
          return;
        }
        if (!counts[instance.type]) {
          counts[instance.type] = { total: 0, bySection: {} };
        }
        counts[instance.type].total += 1;
        if (instance.sectionId) {
          counts[instance.type].bySection[instance.sectionId] =
            (counts[instance.type].bySection[instance.sectionId] || 0) + 1;
        }
      }
    });

    // If new-cases aside is visible, count it as a new-cases widget
    if (hasNewCasesAside) {
      if (!counts['new-cases']) {
        counts['new-cases'] = { total: 0, bySection: {} };
      }
      counts['new-cases'].total += 1;
    }

    return counts;
  }, [widgetInstances, sectionsById, hasNewCasesAside]);

  // Create palette items - include both GeoMap and FocusView as separate entries
  const paletteItems = useMemo(() => {
    const items: Array<{
      id: string;
      widgetType: WidgetType;
      displayName: string;
    }> = [];

    Array.from(widgetRegistry.keys()).forEach((widgetType: WidgetType) => {
      if (widgetType === 'geo-map') {
        items.push({
          id: 'geo-map-center',
          widgetType: 'geo-map',
          displayName: 'Geo Map',
        });
        items.push({
          id: 'geo-map-focus',
          widgetType: 'geo-map',
          displayName: 'Focus View',
        });
      } else {
        items.push({
          id: widgetType,
          widgetType,
          displayName: getWidgetDisplayName(widgetType),
        });
      }
    });

    return items;
  }, []);

  return (
    <Wrapper visible={visible}>
      <Root isExpanded={isExpanded}>
        {paletteItems.map(item => {
          const { id, widgetType } = item;
          const widget = widgetRegistry.get(widgetType);
          if (!widget) {
            return null;
          }

          const counts = widgetCounts[widgetType] || {
            total: 0,
            bySection: {},
          };
          const currentCount = counts.total;

          // Special handling for geo-map items
          let isDisabled: boolean;
          let availableCount: number;

          if (id === 'geo-map-center') {
            const centerCount = counts.bySection.center || 0;
            isDisabled = centerCount >= 1;
            availableCount = Math.max(0, 1 - centerCount);
          } else if (id === 'geo-map-focus') {
            // Focus view has no limit - show as unlimited
            isDisabled = false;
            availableCount = Infinity;
          } else {
            const canAdd = canAddWidget(widgetType, currentCount);
            isDisabled = !canAdd;
            const limit = getWidgetLimit(widgetType);
            availableCount = Math.max(0, limit - currentCount);
          }

          return (
            <WidgetItemWrapper
              key={id}
              className={isDisabled ? classes.itemDisabled : ''}
            >
              <DraggableItem
                id={id as WidgetType}
                sx={{
                  width: 200,
                  height: 150,
                }}
              >
                {getWidgetPreview(widgetType, id)}
              </DraggableItem>

              {/* Show "Limit reached" overlay for disabled widgets */}
              {isDisabled && (
                <Box className={classes.overlayDisabled}>
                  <Typography
                    sx={{
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: 600,
                      textAlign: 'center',
                    }}
                  >
                    Limit reached
                  </Typography>
                </Box>
              )}

              {/* Show available count badge for enabled widgets */}
              {!isDisabled && (
                <Box className={classes.badge}>
                  {availableCount === Infinity
                    ? 'unlimited'
                    : `${availableCount} left`}
                </Box>
              )}
            </WidgetItemWrapper>
          );
        })}
      </Root>
      <IconButton
        title={isExpanded ? 'Hide panel' : 'Show panel'}
        className={classes.toggleButton}
        onClick={handleToggle}
        size="small"
        aria-label={isExpanded ? 'Collapse palette' : 'Expand palette'}
        sx={{
          transform: isExpanded
            ? 'translateX(-50%) rotate(0deg)'
            : 'translateX(-50%) rotate(180deg)',
          transition: 'transform 0.3s ease-in-out',
        }}
      >
        <ExpandMoreIcon />
      </IconButton>
    </Wrapper>
  );
};
